// EditLicense.js
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "../App.css";
import { updateLicense } from "../utils/licensesApi";
import { logAction } from "../utils/logsApi";
import SupportBox from "./SupportBox";
import Modal from "./Modal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRedo } from "@fortawesome/free-solid-svg-icons";

const EditLicense = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const license = location.state?.license;

    const [fields, setFields] = useState({
        expirationDate: "",
        assignedIp: "",
    });
    const [originalFields, setOriginalFields] = useState({});
    const [modal, setModal] = useState({ isOpen: false, title: "", message: "" });

    const userRole = Number(localStorage.getItem("userRole"));

    // Authentication check
    useEffect(() => {
        const token = localStorage.getItem("authToken");
        if (!token) {
            navigate("/");
            return;
        }
    }, [navigate]);

    useEffect(() => {
        if (license) {
            setFields({
                expirationDate: license.expirationDate
                    ? new Date(license.expirationDate).toISOString().split("T")[0]
                    : "",
                assignedIp: license.assignedIp || "",
            });
            setOriginalFields({
                expirationDate: license.expirationDate
                    ? new Date(license.expirationDate).toISOString().split("T")[0]
                    : "",
                assignedIp: license.assignedIp || "",
            });
        } else {
            setModal({
                isOpen: true,
                title: "Error",
                message: "No license data available. Redirecting to licenses list.",
            });
        }
    }, [license]);


    const handleFieldChange = (field, value) => {
        setFields((prev) => ({ ...prev, [field]: value }));
    };


    const handleResetIP = () => {
        if (userRole < 1) {
            setModal({
                isOpen: true,
                title: "Permission Denied",
                message: "Only admins can reset the Assigned IP.",
            });
            return;
        }
        if (license.assignedIp === '') {
            setModal({
                isOpen: true,
                title: "No IP Assigned",
                message: "This license has no IP that is assigned to it.",
            });
            return;
        }

        setFields((prev) => ({ ...prev, assignedIp: "" }));

        setModal({
            isOpen: true,
            title: "Assigned IP Reset",
            message: "Assigned IP has been cleared. Please save to apply changes.",
        });
    };


    const handleSave = async () => {
        const updates = {};

        for (const key in fields) {
            if (fields[key] !== originalFields[key]) {
                updates[key] = fields[key];
            }
        }

        if (Object.keys(updates).length === 0) {
            navigate("/dashboard");
            return;
        }


        if (updates.expirationDate) {
            const today = new Date();
            const newExpirationDate = new Date(updates.expirationDate);
            const differenceInDays = Math.ceil(
                (newExpirationDate - today) / (1000 * 60 * 60 * 24)
            );


            if (userRole < 2 && differenceInDays >= 365) {
                setModal({
                    isOpen: true,
                    title: "Permission Denied",
                    message: "The expiration date exceeds 365 days. Please contact Admins.",
                });
                return;
            }
        }

        try {
            const response = await updateLicense(license.licenseKey, updates);

            if (response.success) {
                localStorage.removeItem("currentLicense");
                setModal({
                    isOpen: true,
                    title: "Success",
                    message: "License updated successfully!",
                });
                logAction(
                    "EDIT_LICENSE",
                    localStorage.getItem("userEmail"),
                    `Updated fields: ${Object.keys(updates).join(", ")}`,
                    license.licenseKey,
                    `Product: ${license.product}`
                );
                navigate("/dashboard");
            } else {
                setModal({
                    isOpen: true,
                    title: "Error",
                    message: `Error updating license: ${response.message}`,
                });
            }
        } catch (error) {
            console.error("Error updating license:", error);
            setModal({
                isOpen: true,
                title: "Error",
                message: "An error occurred while updating the license.",
            });
        }
    };

    return (
        <div className="edit-license-container">
            <h2 className="edit-license-title">Edit License</h2>
            <form className="edit-license-form">
                <div className="form-group">
                    <label>License Key:</label>
                    <input type="text" value={license?.licenseKey || ""} disabled />
                </div>
                <div className="form-group">
                    <label>Owner Full Name:</label>
                    <input type="text" value={license?.licenseOwnerName.toUpperCase() || ""} disabled />
                </div>
                <div className="form-group">
                    <label>Owner ID:</label>
                    <input type="text" value={license?.licenseOwner || ""} disabled />
                </div>
                <div className="form-group">
                    <label>Expiration Date:</label>
                    <input
                        type="date"
                        value={fields.expirationDate}
                        onChange={(e) => handleFieldChange("expirationDate", e.target.value)}
                    />
                </div>
                <div className="form-group ip-reset-group">
                    <label>Assigned IP:</label>
                    <div className="input-with-icon">
                        <input
                            type="text"
                            value={fields.assignedIp}
                            onChange={(e) => handleFieldChange("assignedIp", e.target.value)}
                            disabled
                        />
                        <button
                            type="button"
                            onClick={handleResetIP}
                            className="reset-ip-button"
                            title="Reset Assigned IP"
                        >
                            <FontAwesomeIcon icon={faRedo} />
                        </button>
                    </div>
                </div>
                <div className="form-group">
                    <label>Product:</label>
                    <input type="text" value={license?.product || ""} disabled />
                </div>
                <div className="button-group">
                    <button
                        type="button"
                        onClick={() => navigate("/dashboard")}
                        className="cancel-button"
                    >
                        Cancel
                    </button>
                    <button type="button" onClick={handleSave} className="save-button">
                        Save
                    </button>
                </div>
            </form>
            <SupportBox />
            <Modal
                isOpen={modal.isOpen}
                title={modal.title}
                message={modal.message}
                onClose={() => setModal({ isOpen: false, title: "", message: "" })}
            />
        </div>
    );
};

export default EditLicense;
